/**
 * Org slice (PX-W6.2) — organisation settings, security settings, access
 * groups and the read-only tenant policy.
 *
 * Save semantics: a failed save stores its `ApiError` in the matching
 * `saveError` slot (the form keeps the user's edits and shows the mapped
 * message — never a silent failure); the next attempt clears it first. A
 * successful save re-renders from the response (server truth).
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as orgHttp from './org.http'
import type {
  AccessGroup,
  AccessGroupInput,
  OrgSettings,
  SecuritySettings,
  TenantPolicy
} from './types'

/**
 * Preserve the canonical ApiError: the shared transport's interceptor already
 * maps AxiosErrors to it, so re-mapping through `toApiError` would degrade a
 * real 403 to the network fallback. Only strangers go through the mapper.
 */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

interface OrgKeyed<T> {
  orgId: string | null
  value: T
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchOrgSettings = createAsyncThunk<OrgKeyed<OrgSettings>, void, {
  rejectValue: ApiError
}>(
  'org/fetchOrgSettings',
  async (_, { getState, rejectWithValue }) => {
    try {
      const value = await orgHttp.orgHttp.getSettings()
      return { orgId: orgKey(getState), value }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { org: OrgState }).org.settingsCache, orgKey(getState))
  }
)

export const saveOrgSettings = createAsyncThunk<OrgSettings, OrgSettings, {
  rejectValue: ApiError
}>('org/saveOrgSettings', async (body, { rejectWithValue }) => {
  try {
    return await orgHttp.orgHttp.updateSettings(body)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const fetchSecuritySettings = createAsyncThunk<OrgKeyed<SecuritySettings>, void, {
  rejectValue: ApiError
}>(
  'org/fetchSecuritySettings',
  async (_, { getState, rejectWithValue }) => {
    try {
      const value = await orgHttp.orgHttp.getSecurity()
      return { orgId: orgKey(getState), value }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { org: OrgState }).org.securityCache, orgKey(getState))
  }
)

export const saveSecuritySettings = createAsyncThunk<SecuritySettings, SecuritySettings, {
  rejectValue: ApiError
}>('org/saveSecuritySettings', async (body, { rejectWithValue }) => {
  try {
    return await orgHttp.orgHttp.updateSecurity(body)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const fetchAccessGroups = createAsyncThunk<OrgKeyed<AccessGroup[]>, void, {
  rejectValue: ApiError
}>(
  'org/fetchAccessGroups',
  async (_, { getState, rejectWithValue }) => {
    try {
      const value = await orgHttp.orgHttp.listAccessGroups()
      return { orgId: orgKey(getState), value }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { org: OrgState }).org.groupsCache, orgKey(getState))
  }
)

export const createAccessGroup = createAsyncThunk<AccessGroup, AccessGroupInput, {
  rejectValue: ApiError
}>('org/createAccessGroup', async (input, { rejectWithValue }) => {
  try {
    return await orgHttp.orgHttp.createAccessGroup(input)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const updateAccessGroup = createAsyncThunk<
  AccessGroup,
  { groupId: string; input: AccessGroupInput },
  { rejectValue: ApiError }
>('org/updateAccessGroup', async ({ groupId, input }, { rejectWithValue }) => {
  try {
    return await orgHttp.orgHttp.updateAccessGroup(groupId, input)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const deleteAccessGroup = createAsyncThunk<string, string, { rejectValue: ApiError }>(
  'org/deleteAccessGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      await orgHttp.orgHttp.deleteAccessGroup(groupId)
      return groupId
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export const fetchTenantPolicy = createAsyncThunk<OrgKeyed<TenantPolicy>, void, {
  rejectValue: ApiError
}>(
  'org/fetchTenantPolicy',
  async (_, { getState, rejectWithValue }) => {
    try {
      const value = await orgHttp.orgHttp.tenantPolicy()
      return { orgId: orgKey(getState), value }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { org: OrgState }).org.policyCache, orgKey(getState))
  }
)

// ---------------------------------------------------------------------------
// State + slice
// ---------------------------------------------------------------------------

export interface OrgState {
  settings: OrgSettings | null
  settingsCache: FetchCache
  settingsError: ApiError | null
  savingSettings: boolean
  security: SecuritySettings | null
  securityCache: FetchCache
  securityError: ApiError | null
  savingSecurity: boolean
  groups: AccessGroup[]
  groupsCache: FetchCache
  groupsError: ApiError | null
  savingGroup: boolean
  tenantPolicy: TenantPolicy | null
  policyCache: FetchCache
  policyError: ApiError | null
  /** The most recent save failure, mapped by the form (keeps the edits). */
  saveError: ApiError | null
}

const initialState: OrgState = {
  settings: null,
  settingsCache: initialFetchCache,
  settingsError: null,
  savingSettings: false,
  security: null,
  securityCache: initialFetchCache,
  securityError: null,
  savingSecurity: false,
  groups: [],
  groupsCache: initialFetchCache,
  groupsError: null,
  savingGroup: false,
  tenantPolicy: null,
  policyCache: initialFetchCache,
  policyError: null,
  saveError: null
}

const upsertGroup = (state: OrgState, group: AccessGroup): void => {
  const id = group.id ?? group.slug ?? group.name
  const existing = state.groups.findIndex((row) => (row.id ?? row.slug ?? row.name) === id)
  if (existing >= 0) state.groups[existing] = group
  else state.groups.push(group)
}

export const orgSlice = createSlice({
  name: 'org',
  initialState,
  reducers: {
    /** Clear the surfaced save error (the form was edited again). */
    saveErrorDismissed(state) {
      state.saveError = null
    },
    retryClicked(state) {
      invalidate(state.settingsCache)
      invalidate(state.securityCache)
      invalidate(state.groupsCache)
      invalidate(state.policyCache)
    }
  },
  extraReducers: (builder) => {
    builder
      // ── Org settings ─────────────────────────────────────────────────────
      .addCase(fetchOrgSettings.pending, (state) => {
        fetchStarted(state.settingsCache)
        state.settingsError = null
      })
      .addCase(fetchOrgSettings.fulfilled, (state, action: PayloadAction<OrgKeyed<OrgSettings>>) => {
        fetchSucceeded(state.settingsCache, action.payload.orgId)
        state.settings = action.payload.value
      })
      .addCase(fetchOrgSettings.rejected, (state, action) => {
        fetchFailed(state.settingsCache)
        state.settingsError = action.payload ?? null
      })
      .addCase(saveOrgSettings.pending, (state) => {
        state.savingSettings = true
        state.saveError = null
      })
      .addCase(saveOrgSettings.fulfilled, (state, action: PayloadAction<OrgSettings>) => {
        state.savingSettings = false
        state.settings = action.payload
      })
      .addCase(saveOrgSettings.rejected, (state, action) => {
        state.savingSettings = false
        state.saveError = action.payload ?? null
      })
      // ── Security settings ────────────────────────────────────────────────
      .addCase(fetchSecuritySettings.pending, (state) => {
        fetchStarted(state.securityCache)
        state.securityError = null
      })
      .addCase(
        fetchSecuritySettings.fulfilled,
        (state, action: PayloadAction<OrgKeyed<SecuritySettings>>) => {
          fetchSucceeded(state.securityCache, action.payload.orgId)
          state.security = action.payload.value
        }
      )
      .addCase(fetchSecuritySettings.rejected, (state, action) => {
        fetchFailed(state.securityCache)
        state.securityError = action.payload ?? null
      })
      .addCase(saveSecuritySettings.pending, (state) => {
        state.savingSecurity = true
        state.saveError = null
      })
      .addCase(saveSecuritySettings.fulfilled, (state, action: PayloadAction<SecuritySettings>) => {
        state.savingSecurity = false
        state.security = action.payload
      })
      .addCase(saveSecuritySettings.rejected, (state, action) => {
        state.savingSecurity = false
        state.saveError = action.payload ?? null
      })
      // ── Access groups ────────────────────────────────────────────────────
      .addCase(fetchAccessGroups.pending, (state) => {
        fetchStarted(state.groupsCache)
        state.groupsError = null
      })
      .addCase(fetchAccessGroups.fulfilled, (state, action: PayloadAction<OrgKeyed<AccessGroup[]>>) => {
        fetchSucceeded(state.groupsCache, action.payload.orgId)
        state.groups = action.payload.value
      })
      .addCase(fetchAccessGroups.rejected, (state, action) => {
        fetchFailed(state.groupsCache)
        state.groupsError = action.payload ?? null
      })
      .addCase(createAccessGroup.pending, (state) => {
        state.savingGroup = true
        state.saveError = null
      })
      .addCase(createAccessGroup.fulfilled, (state, action: PayloadAction<AccessGroup>) => {
        state.savingGroup = false
        upsertGroup(state, action.payload)
      })
      .addCase(createAccessGroup.rejected, (state, action) => {
        state.savingGroup = false
        state.saveError = action.payload ?? null
      })
      .addCase(updateAccessGroup.pending, (state) => {
        state.savingGroup = true
        state.saveError = null
      })
      .addCase(updateAccessGroup.fulfilled, (state, action: PayloadAction<AccessGroup>) => {
        state.savingGroup = false
        upsertGroup(state, action.payload)
      })
      .addCase(updateAccessGroup.rejected, (state, action) => {
        state.savingGroup = false
        state.saveError = action.payload ?? null
      })
      .addCase(deleteAccessGroup.pending, (state) => {
        state.savingGroup = true
        state.saveError = null
      })
      .addCase(deleteAccessGroup.fulfilled, (state, action: PayloadAction<string>) => {
        state.savingGroup = false
        state.groups = state.groups.filter(
          (row) => (row.id ?? row.slug ?? row.name) !== action.payload
        )
      })
      .addCase(deleteAccessGroup.rejected, (state, action) => {
        state.savingGroup = false
        state.saveError = action.payload ?? null
      })
      // ── Tenant policy (read-only) ────────────────────────────────────────
      .addCase(fetchTenantPolicy.pending, (state) => {
        fetchStarted(state.policyCache)
        state.policyError = null
      })
      .addCase(fetchTenantPolicy.fulfilled, (state, action: PayloadAction<OrgKeyed<TenantPolicy>>) => {
        fetchSucceeded(state.policyCache, action.payload.orgId)
        state.tenantPolicy = action.payload.value
      })
      .addCase(fetchTenantPolicy.rejected, (state, action) => {
        fetchFailed(state.policyCache)
        state.policyError = action.payload ?? null
      })
  }
})

export const { saveErrorDismissed, retryClicked } = orgSlice.actions
